import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { RegHubTranslocoLoader } from './reg-hub-transloco-loader';
import { LanguageService } from './language.service';
import { EnumLabelService } from '../services/enum-label/enum-label.service';
import {
  REG_HUB_DEFAULT_LANG,
  REG_HUB_DEFAULT_LANGS,
  REG_HUB_TRANSLOCO_CONFIG,
  RegHubTranslocoConfig
} from './reg-hub-transloco-config';

/**
 * Shared Transloco setup for all RegHub apps. Add to the app's root providers and import
 * TranslocoModule wherever templates use *transloco / the transloco pipe.
 */
export function provideRegHubTransloco(config: RegHubTranslocoConfig): EnvironmentProviders {
  const availableLangs = config.availableLangs ?? REG_HUB_DEFAULT_LANGS;
  const defaultLang = config.defaultLang ?? REG_HUB_DEFAULT_LANG;

  return makeEnvironmentProviders([
    { provide: REG_HUB_TRANSLOCO_CONFIG, useValue: { ...config, availableLangs, defaultLang } },
    provideTransloco({
      config: {
        availableLangs,
        defaultLang,
        reRenderOnLangChange: true,
        prodMode: config.prodMode
      },
      loader: RegHubTranslocoLoader
    }),
    // Restore the saved language and wait for its file so translate() is safe anywhere after bootstrap.
    provideAppInitializer(() => {
      inject(LanguageService).restoreSavedLanguage();
      inject(EnumLabelService); // registers the translator used by static Utilities helpers
      const transloco = inject(TranslocoService);
      return firstValueFrom(transloco.load(transloco.getActiveLang())).then(() => undefined, () => undefined);
    })
  ]);
}
